import { placements } from '@ids/position';
import { transitionState } from '@ids/transition';
import styled, { css, keyframes } from 'styled-components';
import { QBCloseIconControl } from '@ids/icon-control';
import { getStyle as get } from '@ids/ddms-styled/comps/tooltip/intuit/light';

const tooltipBackgroundColor = get('background.color');
const tooltipPointerSpacing = '16px';
const tooltipBodyColor = get('body.color');
const tooltipBodyFontStyle = get('body.fontStyle');
const tooltipBodyFontSize = get('body.fontSize');
const tooltipBodyLineHeight = get('body.lineHeight');
const tooltipTitleFontWeight = get('title.fontWeight');
const fontFamily = 'Avenir Next forINTUIT,Avenir,Helvetica,Arial,sans-serif';
const bodyFontWeight = get('body.fontWeight');
const qbBodyFontWeight = '500'; // fontWeightWebMedium
const qbBorderColor = '#8D9096';
const qbLineHeight = '20px';
const qbIconFocusColor = '#34BFFF 0 0 0 2px';
const tooltipIconFocusColor = 'rgba(255, 255, 255, 0.5) 0 0 0 2px';

// All pointers no design data.
const bottomPositionRightAlignment = css`
  margin-top: 18px;
  &::after,
  &::before {
    bottom: 100%;
    border: solid transparent;
    content: '';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    right: ${tooltipPointerSpacing};
  }
  ${props =>
    props.theme === 'quickbooks'
      ? `&::before {
          right: 15px;
          border-color: rgba(${qbBorderColor}, 0);
          border-bottom-color: ${qbBorderColor};
          border-width: 9px;
          margin-left: -9px;
        }`
      : ''}
  &::after {
    border-color: rgba(${tooltipBackgroundColor}, 0);
    border-bottom-color: ${tooltipBackgroundColor};
    border-width: 8px;
    margin-left: -8px;
  }
`;

const bottomPositionLeftAlignment = css`
  margin-top: 18px;
  &::after,
  &::before {
    bottom: 100%;
    border: solid transparent;
    content: '';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }
  ${props =>
    props.theme === 'quickbooks'
      ? `&::before {
          left: 15px;
          border-color: rgba(${qbBorderColor}, 0);
          border-bottom-color: ${qbBorderColor};
          border-width: 9px;
          margin-right: -9px;
        }`
      : ''}
  &::after {
    border-color: rgba(${tooltipBackgroundColor}, 0);
    border-bottom-color: ${tooltipBackgroundColor};
    border-width: 8px;
    margin-right: -8px;
    left: ${tooltipPointerSpacing};
  }
`;

const topPositionRightAlignment = css`
  margin-bottom: 18px;
  &::after,
  &::before {
    top: 100%;
    border: solid transparent;
    content: '';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    right: ${tooltipPointerSpacing};
  }
  ${props =>
    props.theme === 'quickbooks'
      ? `&::before {
          right: 15px;
          border-color: rgba(${qbBorderColor}, 0);
          border-top-color: ${qbBorderColor};
          border-width: 9px;
          margin-left: -9px;
        }`
      : ''}
  &::after {
    border-color: rgba(${tooltipBackgroundColor}, 0);
    border-top-color: ${tooltipBackgroundColor};
    border-width: 8px;
    margin-left: -8px;
  }
`;

const topPositionLeftAlignment = css`
  margin-bottom: ${props => (props.theme === 'quickbooks' ? '16px' : '18px')};
  &::after,
  &::before {
    top: 100%;
    border: solid transparent;
    content: '';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    left: ${tooltipPointerSpacing};
  }
  ${props =>
    props.theme === 'quickbooks'
      ? `&::before {
          left: 15px;
          border-color: rgba(${qbBorderColor}, 0);
          border-top-color: ${qbBorderColor};
          border-width: 9px;
          margin-right: -9px;
        }`
      : ''}
  &::after {
    border-color: rgba(${tooltipBackgroundColor}, 0);
    border-top-color: ${tooltipBackgroundColor};
    border-width: 8px;
    margin-right: -8px;
  }
`;

const leftPositionTopAlignment = css`
  margin-bottom: ${props => (props.theme === 'quickbooks' ? '16px' : '18px')};
  &::before,
  &::after {
    left: 100%;
    top: 50%;
    border: solid transparent;
    content: '';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }
  &::after {
    border-color: rgba(${tooltipBackgroundColor}, 0);
    border-left-color: ${tooltipBackgroundColor};
    border-width: 8px;
    margin-top: -8px;
  }
  ${props =>
    props.theme === 'quickbooks'
      ? ` &::before {
            border-color: rgba(${qbBorderColor}, 0);
            border-left-color: ${qbBorderColor};
            border-width: 9px;
            margin-top: -9px;
          }`
      : ''}
`;

const rightPositionTopAlignment = css`
  margin-left: 18px;
  &::before,
  &::after {
    right: 100%;
    top: 50%;
    border: solid transparent;
    content: '';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }
  &::after {
    border-color: rgba(${tooltipBackgroundColor}, 0);
    border-right-color: ${tooltipBackgroundColor};
    border-width: 8px;
    margin-top: -8px;
  }
  ${props =>
    props.theme === 'quickbooks'
      ? `&::before {
          border-color: rgba(${qbBorderColor}, 0);
          border-right-color: ${qbBorderColor};
          border-width: 9px;
          margin-top: -9px;
        }`
      : ''}
`;

const pointerPlacementMap = {
  [placements.BOTTOM_RIGHT]: bottomPositionRightAlignment,
  [placements.BOTTOM_LEFT]: bottomPositionLeftAlignment,
  [placements.TOP_RIGHT]: topPositionRightAlignment,
  [placements.TOP_LEFT]: topPositionLeftAlignment,
  [placements.LEFT_TOP]: leftPositionTopAlignment,
  [placements.RIGHT_TOP]: rightPositionTopAlignment
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const transitionEnter = css`
  animation: ${fadeIn} 250ms;
`;

const transitionExit = css`
  animation: ${fadeOut} 250ms;
`;

const transitionStateMap = {
  [transitionState.ENTERING]: transitionEnter,
  [transitionState.EXITING]: transitionExit
};

export const Wrapper = styled.div`
  max-width: 260px;
  min-width: 80px;
  position: relative;
  opacity: 1;
  border-radius: 4px;
  pointer-events: none;
  font: ${tooltipBodyFontStyle}
    ${props =>
      props.theme === 'quickbooks' ? qbBodyFontWeight : bodyFontWeight}
    ${tooltipBodyFontSize} ${fontFamily};
  line-height: ${tooltipBodyLineHeight};
  color: ${tooltipBodyColor};
  background-color: ${tooltipBackgroundColor};
  border: ${props =>
    props.theme === 'quickbooks' ? `1px solid ${qbBorderColor}` : ''};
  &::after {
    position: absolute;
    width: 0;
    height: 0;
    margin: 0;
    content: '';
  }

  ${props =>
    pointerPlacementMap[props.placement]
      ? pointerPlacementMap[props.placement]
      : ''}
  ${props => transitionStateMap[props.transitionState]}
`;

export const OuterContainer = styled.div`
  display: flex;
`;

export const Container = styled.div`
  margin: 16px;
  &:focus {
    outline: none;
  }
`;

export const Title = styled.h3`
  font: ${get('title.fontStyle')} ${tooltipTitleFontWeight}
    ${get('title.fontSize')} ${fontFamily};
  line-height: ${props =>
    props.theme === 'quickbooks' ? qbLineHeight : get('title.lineHeight')};
  color: ${get('title.color')};
  margin-top: 0;
  padding-bottom: 4px;
  &:focus {
    outline: none;
  }
  margin-block-end: 0;
`;

export const CloseIconControlWrapper = styled(QBCloseIconControl)`
  svg {
    fill: ${props =>
      props.theme === 'quickbooks' ? qbBorderColor : '#BABEC5'};
  }
  float: right;
  display: block;
  margin-top: 8px;
  margin-right: 8px;
  pointer-events: all;
  &:focus {
    box-shadow: ${props =>
      props.theme === 'quickbooks' ? qbIconFocusColor : tooltipIconFocusColor};
  }
`;

export const InnerWrapper = styled.div`
  color: ${tooltipBodyColor};
  overflow: hidden;
  * {
    color: ${tooltipBodyColor};
  }
  b,
  strong {
    color: ${tooltipBodyColor};
    font-weight: ${tooltipTitleFontWeight};
  }
`;

export const Truncate = styled.div`
  /* stylelint-disable property-no-vendor-prefix, value-no-vendor-prefix */
  @media only screen and (max-width: 480px) {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;

    * {
      margin: 0;
      padding: 0;
    }
  }
`;
